import { useState, useEffect } from "react";
import { X, ImageIcon } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

export default function CourseFormModal({ course, onClose, onSaved }) {
  const isEdit = !!course;
  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    original_price: "",
    thumbnail: "",
    category: "",
  });
  const [imageFile, setImageFile] = useState(null); // eslint-disable-line no-unused-vars
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setForm({
        title: course.title || "",
        description: course.description || "",
        price: course.price || "",
        original_price: course.original_price || "",
        thumbnail: course.thumbnail || "",
        category: course.category || "",
      });
      setPreview(course.thumbnail || "");
    }
  }, [course, isEdit]);

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
    // For now store as base64 or object URL; in production use S3 upload
    const reader = new FileReader();
    reader.onloadend = () => setForm((f) => ({ ...f, thumbnail: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/courses/${course.id}`, form);
        toast.success("Course updated!");
      } else {
        await api.post(
          `/courses?title=${encodeURIComponent(form.title)}&description=${encodeURIComponent(form.description)}&price=${form.price || 0}&original_price=${form.original_price || 0}&thumbnail=${encodeURIComponent(form.thumbnail)}&category=${encodeURIComponent(form.category || "General")}`
        );
        toast.success("Course created!");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving course");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-800">
          <h3 className="text-lg font-bold dark:text-white">
            {isEdit ? "Edit Course" : "Create New Course"}
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Thumbnail Image
            </label>
            <div className="relative">
              {preview ? (
                <div className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-dashed border-blue-400">
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => { setPreview(""); setImageFile(null); setForm(f => ({ ...f, thumbnail: "" })); }}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full aspect-video rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 cursor-pointer hover:border-blue-400 transition-colors bg-slate-50 dark:bg-slate-800/50">
                  <ImageIcon size={32} className="text-slate-400 mb-2" />
                  <span className="text-sm text-slate-500">Click to upload image</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImage} />
                </label>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Course title"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
            <input
              type="text"
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              placeholder="e.g. Banking, SSC, Railway"
              list="admin-category-suggestions"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
            />
            <datalist id="admin-category-suggestions">
              <option value="Banking" />
              <option value="SSC" />
              <option value="Railway" />
              <option value="UPSC" />
              <option value="Insurance" />
              <option value="State Exams" />
              <option value="Kids Learning" />
              <option value="Class 1-5" />
              <option value="Class 6-8" />
              <option value="Class 9-10" />
              <option value="+2 Science" />
              <option value="+2 Commerce" />
              <option value="Graduation" />
              <option value="Competitive Exams" />
              <option value="Coding" />
              <option value="Spoken English" />
              <option value="Digital Skills" />
              <option value="Placement Preparation" />
              <option value="Resume Builder" />
              <option value="Mock Interviews" />
              <option value="Aptitude Tests" />
              <option value="Interview Tracker" />
            </datalist>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Course description..."
              rows={3}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none"
            />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Price (₹)</label>
              <input
                type="number"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                placeholder="0"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Original Price (₹)</label>
              <input
                type="number"
                value={form.original_price}
                onChange={e => setForm(f => ({ ...f, original_price: e.target.value }))}
                placeholder="0"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : isEdit ? "Update Course" : "Create Course"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
